import type { Ref } from 'vue'
import { shallowRef } from 'vue'
import type { UseFetchOptions } from 'nuxt/app'
import { defu } from 'defu'
import { camelCase } from 'camel-case'
import { snakeCase } from 'snake-case'
import { convertKeyRecursively } from '~/utils/convert-key-recursively'

class BaseError extends Error {
  constructor(e?: string) {
    super(e)
    this.name = new.target.name
  }
}

// 足りなければ拡張する
export class ResponseError extends BaseError {
  constructor(
    public statusCode: number,
    public data: Record<string, string> | Record<string, object>,
    e?: string
  ) {
    super(e)
  }
}

export interface CustomUseFetchOptions<T> extends UseFetchOptions<T> {
  skipConvertKeyRecursivelyOnResponse?: boolean
}

// useFetch 互換の戻り形(await 後に data.value / error.value を読む契約)。
// error には ResponseError(HTTP エラー) のほか、408(タイムアウト)やネットワークエラーも
// 入る。いずれも statusCode/message/data を持つ形で消費される(requestOrThrow 等)。
export interface CustomFetchResult<T> {
  data: Ref<T | undefined>
  error: Ref<ResponseError | undefined>
}

// API 呼び出しの共通ラッパ。内部は useFetch ではなく $fetch を使う:
// SPA(ssr:false) では useFetch の payload キャッシュ/オートキー/dedupe に利点が無く、
// 更新系の後に安定キーが古い応答を返す事故(削除や変更が画面に反映されない)だけが残るため、
// 呼ぶたびに必ずリクエストする。状態のキャッシュは各 composable のモジュール ref が担う。
export async function useCustomFetch<T>(
  path: string,
  options: CustomUseFetchOptions<T> = {}
): Promise<CustomFetchResult<T>> {
  const timeout = 40000 // (ms)
  const config = useRuntimeConfig()
  const { auth } = useAuth()

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)

  const defaults: CustomUseFetchOptions<T> = {
    baseURL: config.public.apiBaseUrl,
    signal: controller.signal,
    headers: { Authorization: `Bearer ${auth.value.sessionId ? auth.value.sessionId : null}` },

    onRequest({ options }) {
      // convertKeyRecursively は unknown を返すため $fetch の各 slot 型に narrow して戻す。
      // 中身が key 変換だけで shape は同型のため安全。
      options.body = convertKeyRecursively(options.body, snakeCase) as
        | Record<string, unknown>
        | BodyInit
        | null
        | undefined
      options.query = convertKeyRecursively(options.query, snakeCase) as
        | Record<string, unknown>
        | undefined
      options.params = convertKeyRecursively(options.params, snakeCase) as
        | Record<string, unknown>
        | undefined
    },

    onResponse({ response }) {
      // エラー応答の body は変換せず wire(snake_case) のまま ResponseError.data へ渡す契約。
      if (!response.ok) return
      if (!options.skipConvertKeyRecursivelyOnResponse)
        response._data = convertKeyRecursively(response._data, camelCase) as T
    },

    onRequestError(context) {
      if (context?.error?.name === 'AbortError') {
        throw createError({
          statusCode: 408,
          statusMessage: 'Request timeout.',
          fatal: true,
        })
      }
    },

    onResponseError({ response }) {
      // _data は本来 success shape T 用のため error 側 (Rails の error JSON) には
      // 流せない。consumer 互換のため境界でだけ ResponseError.data 形に narrow する。
      const errorData = (response?._data ?? {}) as Record<string, string> | Record<string, object>
      throw new ResponseError(response?.status, errorData, response?.statusText)
    },
  }

  const params = defu(defaults, options)

  try {
    // UseFetchOptions は $fetch オプションの上位集合(reactive 値や useFetch 固有オプションは
    // 全呼び出し箇所で未使用)のため、そのまま $fetch へ渡す。
    const data = await $fetch<T>(path, params as Parameters<typeof $fetch>[1])
    return { data: shallowRef(data) as Ref<T>, error: shallowRef(undefined) }
  } catch (e) {
    // useFetch 互換: 例外にせず error ref で返す(呼び出し側が createError へ変換して投げる)。
    return { data: shallowRef(undefined) as Ref<T | undefined>, error: shallowRef(e as ResponseError) }
  } finally {
    clearTimeout(timer)
  }
}
